import { useMemo, useState } from 'react';
import type { Lead, Turma } from '@/mocks/admin';
import TurmaDetalheModal from './TurmaDetalheModal';
import LeadDetalheModal from './LeadDetalheModal';

interface AdminDashboardProps {
  leads: Lead[];
  turmas: Turma[];
  onStatusChange: (leadId: string, status: Lead['status']) => void;
  onTurmaChange: (leadId: string, turmaId: string | null) => void;
}

const statusTurmaConfig: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  em_formacao: { label: 'Em formação', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-400' },
  completa: { label: 'Completa', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-400' },
  encerrada: { label: 'Encerrada', color: 'text-gray-400', bg: 'bg-gray-100 border-gray-200', dot: 'bg-gray-400' },
};

const nivelConfig: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'Em Risco', color: 'text-red-600', bg: 'bg-red-50' },
  2: { label: 'Desorganizado', color: 'text-orange-600', bg: 'bg-orange-50' },
  3: { label: 'Estruturando', color: 'text-amber-600', bg: 'bg-amber-50' },
  4: { label: 'Escalável', color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

export default function AdminDashboard({ leads, turmas, onStatusChange, onTurmaChange }: AdminDashboardProps) {
  const [turmaAberta, setTurmaAberta] = useState<(Turma & { leads: Lead[] }) | null>(null);
  const [leadSelecionado, setLeadSelecionado] = useState<Lead | null>(null);

  const stats = useMemo(() => {
    const total = leads.length;
    const novos = leads.filter((l) => l.status === 'novo').length;
    const confirmados = leads.filter((l) => l.status === 'confirmado').length;
    const desistiram = leads.filter((l) => l.status === 'desistiu').length;
    const comDiagnostico = leads.filter((l) => l.diagnostico !== null).length;
    const comPrograma = leads.filter((l) => l.origens.includes('programa')).length;
    const taxaConversao = comDiagnostico > 0 ? Math.round((comPrograma / comDiagnostico) * 100) : 0;
    return { total, novos, confirmados, desistiram, comDiagnostico, comPrograma, taxaConversao };
  }, [leads]);

  const turmasComLeads = useMemo(() =>
    turmas.map((t) => ({
      ...t,
      leads: leads.filter((l) => l.turmaId === t.id),
    })), [leads, turmas]);

  const distribuicaoNiveis = useMemo(() => {
    const dist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
    leads.forEach((l) => {
      if (l.diagnostico) dist[l.diagnostico.nivelNumero] = (dist[l.diagnostico.nivelNumero] || 0) + 1;
    });
    return dist;
  }, [leads]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-8">
      {/* Cards de resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total de Leads', value: stats.total, icon: 'ri-user-line', color: 'text-gray-700', bg: 'bg-white' },
          { label: 'Novos', value: stats.novos, icon: 'ri-user-add-line', color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Confirmados', value: stats.confirmados, icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Desistiram', value: stats.desistiram, icon: 'ri-user-unfollow-line', color: 'text-red-500', bg: 'bg-red-50' },
          { label: 'Com Diagnóstico', value: stats.comDiagnostico, icon: 'ri-bar-chart-2-line', color: 'text-indigo-600', bg: 'bg-indigo-50' },
        ].map((card) => (
          <div key={card.label} className={`${card.bg} border border-gray-100 rounded-2xl p-5`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">{card.label}</span>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100">
                <i className={`${card.icon} ${card.color} text-sm`} />
              </div>
            </div>
            <p className={`text-4xl font-black ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Taxa de conversão */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-amber-900 font-black text-base mb-1">Taxa de Conversão</h3>
            <p className="text-amber-600 text-xs font-medium">Diagnóstico → Interessado TR60D</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-black text-amber-600">{stats.taxaConversao}%</p>
            <p className="text-amber-500 text-xs font-semibold">{stats.comPrograma} de {stats.comDiagnostico} leads</p>
          </div>
        </div>
        {/* Barra de funil */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-amber-700 text-xs font-semibold">Diagnosticaram</span>
              <span className="text-amber-700 text-xs font-bold">{stats.comDiagnostico}</span>
            </div>
            <div className="w-full h-3 bg-amber-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>
          <i className="ri-arrow-right-line text-amber-400 text-lg flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-emerald-700 text-xs font-semibold">Interessados TR60D</span>
              <span className="text-emerald-700 text-xs font-bold">{stats.comPrograma}</span>
            </div>
            <div className="w-full h-3 bg-emerald-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                style={{ width: `${stats.taxaConversao}%` }}
              />
            </div>
          </div>
        </div>
        <p className="text-amber-600 text-xs mt-3 text-center font-medium">
          A cada 100 leads que fazem o diagnóstico, <strong className="text-amber-800">{stats.taxaConversao}</strong> manifestam interesse no programa.
        </p>
      </div>

      {/* Turmas */}
      <div>
        <h2 className="text-gray-900 font-black text-lg mb-4">Turmas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {turmasComLeads.map((turma) => {
            const cfg = statusTurmaConfig[turma.status];
            const ocupacao = turma.leads.length;
            const pct = Math.round((ocupacao / turma.maxVagas) * 100);
            const vagasRestantes = turma.maxVagas - ocupacao;
            const confirmados = turma.leads.filter((l) => l.status === 'confirmado').length;

            return (
              <div
                key={turma.id}
                className="bg-white border border-gray-100 rounded-2xl p-6 cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-all duration-200 group"
                onClick={() => setTurmaAberta(turma)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-gray-900 font-black text-base leading-tight group-hover:text-amber-700 transition-colors">{turma.nome}</h3>
                    <p className="text-gray-400 text-xs mt-1">
                      {formatDate(turma.dataInicio)} → {formatDate(turma.dataFim)}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </span>
                </div>

                {/* Barra de progresso */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-gray-500 text-xs font-medium">Preenchimento</span>
                    <span className="text-gray-700 text-xs font-bold">{ocupacao}/{turma.maxVagas} vagas</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-gray-400'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-gray-400 text-xs">{pct}% preenchido</span>
                    {vagasRestantes > 0 ? (
                      <span className="text-amber-600 text-xs font-semibold">{vagasRestantes} vaga{vagasRestantes !== 1 ? 's' : ''} restante{vagasRestantes !== 1 ? 's' : ''}</span>
                    ) : (
                      <span className="text-emerald-600 text-xs font-semibold">Turma completa!</span>
                    )}
                  </div>
                </div>

                {/* Mini stats */}
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-50">
                  <div className="text-center">
                    <p className="text-gray-900 font-black text-lg">{ocupacao}</p>
                    <p className="text-gray-400 text-xs">Leads</p>
                  </div>
                  <div className="text-center">
                    <p className="text-emerald-600 font-black text-lg">{confirmados}</p>
                    <p className="text-gray-400 text-xs">Confirmados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-amber-600 font-black text-lg">{vagasRestantes}</p>
                    <p className="text-gray-400 text-xs">Restantes</p>
                  </div>
                </div>

                {/* Ver mentorados hint */}
                <div className="mt-4 pt-3 border-t border-gray-50 flex items-center justify-center gap-1.5 text-gray-400 group-hover:text-amber-600 transition-colors">
                  <i className="ri-team-line text-sm" />
                  <span className="text-xs font-semibold">Ver mentorados</span>
                  <i className="ri-arrow-right-s-line text-sm" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribuição por nível */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6">
        <h2 className="text-gray-900 font-black text-lg mb-5">Distribuição por nível de diagnóstico</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((nivel) => {
            const cfg = nivelConfig[nivel];
            const count = distribuicaoNiveis[nivel] || 0;
            const total = leads.filter((l) => l.diagnostico).length;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={nivel} className={`${cfg.bg} rounded-xl p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wider ${cfg.color}`}>Nível {nivel}</span>
                  <span className={`text-xs font-semibold ${cfg.color}`}>{pct}%</span>
                </div>
                <p className={`text-3xl font-black ${cfg.color} mb-1`}>{count}</p>
                <p className="text-gray-500 text-xs">{cfg.label}</p>
                <div className="w-full h-1.5 bg-white/60 rounded-full mt-3 overflow-hidden">
                  <div className={`h-full rounded-full ${cfg.color.replace('text-', 'bg-')}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Últimos leads */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6">
        <h2 className="text-gray-900 font-black text-lg mb-5">Últimos leads cadastrados</h2>
        <div className="space-y-3">
          {[...leads]
            .sort((a, b) => new Date(b.dataCadastro).getTime() - new Date(a.dataCadastro).getTime())
            .slice(0, 5)
            .map((lead) => (
              <div key={lead.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                  <span className="text-gray-600 text-sm font-bold">{lead.nome.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-sm font-bold truncate">{lead.nome}</p>
                  <p className="text-gray-400 text-xs truncate">{lead.empresa}</p>
                </div>
                {lead.diagnostico && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${nivelConfig[lead.diagnostico.nivelNumero].bg} ${nivelConfig[lead.diagnostico.nivelNumero].color}`}>
                    N{lead.diagnostico.nivelNumero}
                  </span>
                )}
                <span className="text-gray-400 text-xs whitespace-nowrap">
                  {new Date(lead.dataCadastro).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Modal de turma */}
      {turmaAberta && (
        <TurmaDetalheModal
          turma={turmaAberta}
          onClose={() => setTurmaAberta(null)}
          onVerLead={(lead) => {
            setTurmaAberta(null);
            setLeadSelecionado(lead);
          }}
        />
      )}

      {/* Modal de lead (aberto a partir do modal de turma) */}
      {leadSelecionado && (
        <LeadDetalheModal
          lead={leadSelecionado}
          turmas={turmas}
          onClose={() => setLeadSelecionado(null)}
          onStatusChange={(id, status) => {
            onStatusChange(id, status);
            setLeadSelecionado((prev) => prev ? { ...prev, status } : null);
          }}
          onTurmaChange={(id, turmaId) => {
            onTurmaChange(id, turmaId);
            setLeadSelecionado((prev) => prev ? { ...prev, turmaId } : null);
          }}
        />
      )}
    </div>
  );
}
